import { ReactElement, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { organisationActions } from '../../redux/slices/organisation.slice';
import { RootState } from '../../redux/store';
import { ROUTES } from '../../helpers/constans';
import OrganisationAccessPanel from '../../components/organisation-access-panel/organisation-access-panel';
import OrganisationTeamsPanel from '../../components/organisation-teams-panel/organisation-teams-panel';
import OrganisationEventsPanel from '../../components/organisation-events-panel/organisation-events-panel';
import OrganisationGraphicsPanel from '../../components/organisation-graphics-panel';
import OrganisationSocialsPanel from '../../components/organisation-socials-panel';
import { IOrganisation } from '../../types/@organisation';

type TOrganisationTab = 'events' | 'graphics' | 'teams' | 'access' | 'details' | 'socials';

const OrganisationManage = (): ReactElement => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    all,
    current,
    access,
    teams,
    events,
    isLoadingAccess,
    isLoadingTeams,
    isLoadingEvents,
    isSavingAccess,
  } = useSelector((state: RootState) => state.organisations);
  const currentUserId = useSelector((state: RootState) => state.auth.id);

  const [tab, setTab] = useState<TOrganisationTab>('events');
  const [socialMessage, setSocialMessage] = useState('');

  useEffect(() => {
    if (!all.length) {
      dispatch(organisationActions.getOrganisations(undefined));
    }
  }, [all.length, dispatch]);

  useEffect(() => {
    if (id) {
      dispatch(organisationActions.getOrganisationAccess({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && tab === 'teams') {
      dispatch(organisationActions.getOrganisationTeams({ id }));
    }
  }, [dispatch, id, tab]);

  useEffect(() => {
    if (id && (tab === 'events' || tab === 'graphics')) {
      dispatch(organisationActions.getOrganisationEvents({ id }));
    }
  }, [dispatch, id, tab]);

  useEffect(() => {
    if (!id) return;
    if (current?._id === id) return;

    const foundOrg = (all as IOrganisation[]).find((organisation) => organisation._id === id) || null;
    if (foundOrg) {
      dispatch(organisationActions.setCurrentOrganisation(foundOrg));
    }
  }, [all, current, dispatch, id]);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedTab = params.get('tab');
    const platform = params.get('platform') || 'social account';
    const status = params.get('status');
    const reason = params.get('reason');
    if (requestedTab === 'socials') setTab('socials');
    if (status === 'connected') setSocialMessage(`${platform} connected successfully.`);
    else if (status === 'failed') setSocialMessage(`${platform} connection failed${reason ? `: ${reason.replace(/_/g, ' ')}` : '.'}`);
  }, [location.search]);
  const organisationTitle = useMemo(() => {
    if (current?._id === id && current?.name) {
      return current.name;
    }

    const foundOrg = (all as IOrganisation[]).find((organisation) => organisation._id === id);
    return foundOrg?.name || 'Organisation management';
  }, [all, current, id]);

  const handleBack = (): void => {
    navigate(ROUTES.organisations);
  };

  const handleTabChange = (_event: SyntheticEvent, value: TOrganisationTab): void => {
    setTab(value);
  };

  return (
    <Box
      sx={{
        width: '100%',
        p: 4,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1200px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '32px', fontWeight: 700, color: '#17324d' }}>
              {organisationTitle}
            </Typography>
            <Typography sx={{ fontSize: '16px', color: '#5f7285', mt: 1 }}>
              Manage organisation access, members, teams, events, admins, and helpers.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{ textTransform: 'none', borderRadius: '12px', px: 3 }}
          >
            Back
          </Button>
        </Box>

        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '18px',
            boxShadow: '0 4px 20px rgba(23,50,77,0.08)',
            overflow: 'hidden',
          }}
        >
          <Tabs value={tab} onChange={handleTabChange}>
            <Tab value="events" label="Events" />
            <Tab value="graphics" label="Graphics" />
            <Tab value="teams" label="Teams" />
            <Tab value="access" label="Access" />
            <Tab value="details" label="Details" />
            <Tab value="socials" label="Socials" />
          </Tabs>

          <Divider />

          <Box sx={{ p: 3 }}>
            {tab === 'events' && (
              <>
                {isLoadingEvents && !events ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : events ? (
                  <OrganisationEventsPanel events={events} />
                ) : (
                  <Alert severity="warning">
                    Events data could not be loaded for this organisation.
                  </Alert>
                )}
              </>
            )}


            {tab === 'graphics' && (
              <>
                {isLoadingEvents && !events ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : events ? (
                  <OrganisationGraphicsPanel
                    organisationName={organisationTitle}
                    events={events}
                  />
                ) : (
                  <Alert severity="warning">
                    Events data could not be loaded for this organisation.
                  </Alert>
                )}
              </>
            )}

            {tab === 'teams' && (
              <>
                {isLoadingTeams && !teams ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : teams ? (
                  <OrganisationTeamsPanel teams={teams} />
                ) : (
                  <Alert severity="warning">
                    Teams data could not be loaded for this organisation.
                  </Alert>
                )}
              </>
            )}

            {tab === 'access' && (
              <>
                {isLoadingAccess && !access ? (
                  <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                ) : access ? (
                  <OrganisationAccessPanel
                    organisationId={id}
                    access={access}
                    currentUserId={currentUserId}
                    isSaving={isSavingAccess}
                  />
                ) : (
                  <Alert severity="warning">
                    Access data could not be loaded for this organisation.
                  </Alert>
                )}
              </>
            )}

            {tab === 'socials' && (
              <OrganisationSocialsPanel organisationId={id} callbackMessage={socialMessage} />
            )}

            {tab === 'details' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
                  Organisation details
                </Typography>

                {current ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '14px',
                        border: '1px solid #e3e8ef',
                        background: '#fafbfd',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Name</Typography>
                      <Typography sx={{ fontSize: '16px', color: '#17324d', fontWeight: 600 }}>
                        {current.name || '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '14px',
                        border: '1px solid #e3e8ef',
                        background: '#fafbfd',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Twitter</Typography>
                      <Typography sx={{ fontSize: '16px', color: '#17324d', fontWeight: 600 }}>
                        {current.twitterId || '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '14px',
                        border: '1px solid #e3e8ef',
                        background: '#fafbfd',
                        gridColumn: { xs: 'auto', md: '1 / span 2' },
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Description</Typography>
                      <Typography sx={{ fontSize: '16px', color: '#17324d', fontWeight: 600 }}>
                        {current.description || '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '14px',
                        border: '1px solid #e3e8ef',
                        background: '#fafbfd',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Instagram</Typography>
                      <Typography sx={{ fontSize: '16px', color: '#17324d', fontWeight: 600 }}>
                        {current.instagramId || '-'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '14px',
                        border: '1px solid #e3e8ef',
                        background: '#fafbfd',
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Image</Typography>
                      {current.image?.path ? (
                        <Chip label="Image uploaded" color="success" />
                      ) : (
                        <Chip label="No image" />
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="info">Organisation details are not available.</Alert>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganisationManage;